import type { BigDecimal } from "@solomon-labs/bigdecimal";
import {
    ArrowShortLeftIcon,
    CrossIcon,
    TooltipIcon
} from "@solomon-labs/design/components/icons";
import {
    Button,
    Heading,
    Text,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from "@solomon-labs/design/components/ui";
import React, { useEffect, useRef, useState } from "react";

import LegacySUSDvIcon from "../../assets/icons/LegacySUSDv.png";
import LegacyUSDvIcon from "../../assets/icons/LegacyUSDv.png";
import UsdcIcon from "../../assets/icons/USDC.svg";
import USDvIcon from "../../assets/logos/usdv-logomark.png";
import { AmountInput } from "../primitives/AmountInput";
import { ReceiptBlock } from "../primitives/ReceiptBlock";
import { TokenChip } from "../primitives/TokenChip";

import {
    formatUsdvMigrationAmount,
    formatUsdvMigrationDateTime,
    getUsdvMigrationCurrentStep
} from "./model";
import type { UsdvMigrationStep } from "./model";
import type { UsdvMigrationPlan } from "./types";

/** Where the step's transaction is, as the host sees it. */
export type UsdvMigrationFlowPhase =
    | "idle"
    | "pending"
    | "processing"
    | "success"
    | "error";

/**
 * Migration mode — the account menu handed over to one step at a time.
 *
 * The card tells the user how many steps are left; this walks them. Only the
 * current step is shown, so the popup stays focused on the single decision in
 * front of the user. There is no stored progress: the step is derived from the
 * balances, so closing the popup and reopening resumes wherever the chain
 * actually is.
 *
 * The one exception is the beat after a step lands. Because the step is
 * derived, a settled transaction advances the screen in the same render that
 * confirms it, which would leave the success unseen. The flow holds the
 * completed step for SUCCESS_DWELL_MS, reports on the CTA, and only then reads
 * the new balances.
 */
export interface UsdvMigrationFlowProps {
    plan: UsdvMigrationPlan;
    onStepViewed?: (
        index: number,
        state: "ready" | "pending" | "failed" | "succeeded" | "cooldown",
        remaining: number
    ) => void;
    /**
     * Leave migration mode and return to the account menu. Also called by the
     * flow itself once the last step's success beat is over — see
     * `useUsdvMigrationSuccessDwell`.
     */
    onBack: (reason?: "back" | "close_button" | "completed") => void;
    /** Run the current step. */
    onAction: () => void | Promise<void>;
    /** When the cooling withdrawal becomes claimable. */
    coolingEndsAt?: Date | null;
    /**
     * Where the step's transaction is. Drives the CTA the same way the Stake,
     * Unstake and Swap forms drive theirs — status="pending" while it is in
     * flight, "error" when it failed. Same in-place convention: the screen
     * stays mounted, the action reports.
     */
    phase?: UsdvMigrationFlowPhase;
    disabled?: boolean;
    error?: string | null;
    receiveAmount?: string;
    minimumReceiveAmount?: string;
    /** The wallet's USDv, shown on the Migration Complete screen. */
    usdvBalance?: BigDecimal | null;
    /**
     * The walk is over and the user is leaving the Migration Complete screen.
     * The host clears its completion state here, so the account menu it
     * returns to carries no migration card.
     */
    onComplete?: () => void;
}

/**
 * The step's receipt — what moves, and what lands. Same shape the Swap and
 * Stake forms show: detail rows, a divider, then the settled summary.
 *
 * Only values the migration plan actually carries appear here. Quote-derived
 * rows the swap widget shows — price, price impact, routing fee, network cost
 * — need a real route quote, which this flow does not have yet.
 */
interface StepReceipt {
    rows: Array<{ label: string; value: string }>;
    summaryLabel: string;
    summaryValue: string;
    /** Marked in the summary with a superscript and printed on the card floor. */
    footnote?: string;
}

function getStepReceipt(
    step: UsdvMigrationStep,
    plan: UsdvMigrationPlan,
    amount: string,
    cooldownLeft: string
): StepReceipt {
    switch (step.index) {
        case 1: {
            const receives = formatUsdvMigrationAmount(
                plan.expectedStakedUsdv ?? plan.stakedUsdv
            );
            return {
                rows: [
                    { label: "Unstaking", value: `${amount} Legacy sUSDv` },
                    { label: "Cooldown", value: `${plan.cooldownDays} days` }
                ],
                summaryLabel: "Claimable\u00b9",
                summaryValue: `${receives} Legacy USDv`,
                footnote: `\u00b9 Must wait ${plan.cooldownDays} days before claiming.`
            };
        }
        case 2:
            return {
                rows: [
                    { label: "Claiming", value: `${amount} Legacy USDv` },
                    { label: "Cooldown Left", value: cooldownLeft }
                ],
                summaryLabel: "You Receive",
                summaryValue: `${amount} Legacy USDv`
            };
        case 3:
            return {
                rows: [{ label: "Swapping", value: `${amount} Legacy USDv` }],
                summaryLabel: "You Receive",
                summaryValue: `${amount} USDC`
            };
        default:
            return {
                rows: [{ label: "Swapping", value: `${amount} USDC` }],
                summaryLabel: "You Receive",
                summaryValue: `${amount} USDv`
            };
    }
}

/** The step's verb while its transaction is in flight. */
const STEP_ACTION_PENDING: Record<string, string> = {
    Unstake: "Unstaking",
    Claim: "Claiming",
    Swap: "Swapping"
};

/** The step's verb once its transaction has settled. */
const STEP_ACTION_DONE: Record<string, string> = {
    Unstake: "Unstaked",
    Claim: "Claimed",
    Swap: "Swapped"
};

/**
 * How long the settled step stays on screen before the flow releases to the
 * next one. Long enough to read the confirmation, short enough that it does
 * not read as a screen the user has to dismiss.
 */
const SUCCESS_DWELL_MS = 3000;

/**
 * The last step has no next step to release to, so its beat ends on the
 * Migration Complete screen instead. Shorter than the between-step dwell:
 * that screen carries the result from there.
 */
const FINAL_DWELL_MS = 2000;

/** How long Migration Complete holds before handing back to the account. */
const COMPLETE_SCREEN_MS = 4000;

function isInFlight(phase: UsdvMigrationFlowPhase): boolean {
    return phase === "pending" || phase === "processing";
}

interface DwellTracker {
    phase: UsdvMigrationFlowPhase;
    /** The balances the in-flight step is spending, frozen at hand-off. */
    inFlightPlan: UsdvMigrationPlan | null;
}

/**
 * Holds the step that just settled on screen for its success beat.
 *
 * The step is derived from the balances, never stored, so the instant a
 * transaction lands the plan advances and this component would already be
 * rendering the NEXT step. That would swallow the success entirely: the screen
 * that did the work is gone before it can report. So the plan the step ran
 * against is captured when the transaction goes in flight, and kept as the
 * render source for SUCCESS_DWELL_MS after it settles. Everything downstream —
 * heading, amount, chip, receipt, CTA — reads `displayPlan`, so freezing one
 * value freezes the whole screen.
 *
 * The transition is detected during render rather than in an effect. An effect
 * commits a frame late, and that frame is the next step painted — the flash the
 * dwell exists to prevent.
 */
function useUsdvMigrationSuccessDwell(
    plan: UsdvMigrationPlan,
    phase: UsdvMigrationFlowPhase,
    onFinalDwellEnd: () => void
): { displayPlan: UsdvMigrationPlan; succeeded: boolean } {
    const [tracker, setTracker] = useState<DwellTracker>(() => ({
        phase,
        inFlightPlan: isInFlight(phase) ? plan : null
    }));
    const [heldPlan, setHeldPlan] = useState<UsdvMigrationPlan | null>(null);

    if (tracker.phase !== phase) {
        // A step succeeded when it leaves flight for anything but an error, or
        // when a caller reports success outright without an in-flight phase.
        const settled =
            !isInFlight(phase) &&
            phase !== "error" &&
            (isInFlight(tracker.phase) || phase === "success");
        setTracker({
            phase,
            inFlightPlan: isInFlight(phase) ? plan : null
        });
        if (settled) setHeldPlan(tracker.inFlightPlan ?? plan);
        else if (heldPlan !== null) setHeldPlan(null);
    }

    // Kept in a ref so a host passing a fresh closure each render cannot
    // restart the timer.
    const onFinalDwellEndRef = useRef(onFinalDwellEnd);
    useEffect(() => {
        onFinalDwellEndRef.current = onFinalDwellEnd;
    });
    useEffect(() => {
        if (heldPlan === null) return;
        // The step that settled was the walk's last when nothing followed it.
        // It exits straight to the account menu without waiting for the live
        // balances to catch up — the swap has landed, and a lagging read must
        // not strand the user on a spent screen.
        const final = getUsdvMigrationCurrentStep(heldPlan)?.total === 1;
        const timer = setTimeout(
            final
                ? (): void => onFinalDwellEndRef.current()
                : (): void => setHeldPlan(null),
            final ? FINAL_DWELL_MS : SUCCESS_DWELL_MS
        );
        return (): void => clearTimeout(timer);
    }, [heldPlan]);

    return { displayPlan: heldPlan ?? plan, succeeded: heldPlan !== null };
}

const STEP_ICON: Record<number, string> = {
    1: LegacySUSDvIcon,
    2: LegacyUSDvIcon,
    3: LegacyUSDvIcon,
    4: UsdcIcon
};

/**
 * The token on the chip beside the amount: what the step SPENDS, the same as
 * the swap widget's input chip. Steps 1-3 move legacy value and name it as
 * such; step 4 spends the USDC that step 3 produced.
 */
const STEP_SYMBOL: Record<number, string> = {
    1: "Legacy sUSDv",
    2: "Legacy USDv",
    3: "Legacy USDv",
    4: "USDC"
};

/** Show only the largest useful unit, with a live seconds countdown at the end. */
function formatCooldownLeft(remainingMs: number): string {
    const seconds = Math.max(0, Math.ceil(remainingMs / 1000));
    const units = [
        { size: 86400, label: "day" },
        { size: 3600, label: "hour" },
        { size: 60, label: "minute" },
        { size: 1, label: "second" }
    ];
    const unit = units.find(({ size }) => seconds >= size) ?? units[3];
    const value = Math.floor(seconds / unit.size);
    return `${value} ${unit.label}${value === 1 ? "" : "s"}`;
}

export function UsdvMigrationFlow({
    plan,
    onBack,
    onAction,
    coolingEndsAt,
    phase = "idle",
    disabled = false,
    error,
    receiveAmount,
    minimumReceiveAmount,
    usdvBalance,
    onComplete,
    onStepViewed
}: UsdvMigrationFlowProps): React.ReactElement | null {
    const dwelledRef = useRef(false);
    // Past the last step: the flow shows Migration Complete until it hands
    // back, whatever the balances read in the meantime.
    const [complete, setComplete] = useState(false);
    const { displayPlan, succeeded } = useUsdvMigrationSuccessDwell(
        plan,
        phase,
        (): void => {
            dwelledRef.current = false;
            setComplete(true);
        }
    );
    const step = getUsdvMigrationCurrentStep(displayPlan);
    const finished = step === null;
    // `step.total` counts the steps LEFT, so it shrinks as the walk advances.
    // The walk's length is the most it has been since this flow opened; the
    // position is how far into that the current step sits. Reopening resumes
    // from the balances, so a reopened walk counts from its new first step.
    const [walkTotal, setWalkTotal] = useState(step?.total ?? 0);
    if (step && step.total > walkTotal) setWalkTotal(step.total);
    const stepTotal = Math.max(walkTotal, step?.total ?? 0);
    const stepPosition = step ? stepTotal - step.total + 1 : 0;
    const [now, setNow] = useState(Date.now);
    const claimEndsAt =
        step?.index === 2 && step.waiting
            ? (coolingEndsAt ?? displayPlan.coolingEndsAt)?.getTime()
            : undefined;
    useEffect(() => {
        setNow(Date.now());
        if (claimEndsAt === undefined || claimEndsAt <= Date.now()) return;
        const timer = setInterval(() => {
            const currentTime = Date.now();
            setNow(currentTime);
            if (currentTime >= claimEndsAt) clearInterval(timer);
        }, 1000);
        return (): void => clearInterval(timer);
    }, [claimEndsAt]);

    // Leaving after the walk is over also clears the host's completion
    // state, however the user leaves: the timer, Back, or Close.
    const leave = (reason: "back" | "close_button" | "completed"): void => {
        if (complete) onComplete?.();
        onBack(reason);
    };
    // Kept in a ref so a host passing a fresh closure each render cannot
    // restart the hand-back timer.
    const leaveRef = useRef(leave);
    useEffect(() => {
        leaveRef.current = leave;
    });
    useEffect(() => {
        if (!complete) return;
        const timer = setTimeout(
            () => leaveRef.current("completed"),
            COMPLETE_SCREEN_MS
        );
        return (): void => clearTimeout(timer);
    }, [complete]);

    // The last step reaches Migration Complete from its own dwell
    // (FINAL_DWELL_MS). This catches a step that was not expected to be last
    // but left nothing behind.
    useEffect(() => {
        if (succeeded) dwelledRef.current = true;
    }, [succeeded]);
    useEffect(() => {
        if (!finished || !dwelledRef.current) return;
        dwelledRef.current = false;
        setComplete(true);
    }, [finished]);

    const viewedState =
        phase === "error"
            ? "failed"
            : isInFlight(phase)
              ? "pending"
              : succeeded
                ? "succeeded"
                : step?.waiting
                  ? "cooldown"
                  : "ready";
    // Keyed on the step's position, not the object: `step` is re-derived
    // from the plan every render.
    const viewedIndex = step?.index;
    const viewedTotal = step?.total;
    useEffect(() => {
        if (viewedIndex !== undefined && viewedTotal !== undefined && !complete)
            onStepViewed?.(viewedIndex, viewedState, viewedTotal);
    }, [viewedIndex, viewedTotal, viewedState, onStepViewed, complete]);

    const header = (
        <div className="flex items-center justify-between gap-2">
            <button
                type="button"
                onClick={(): void => leave("back")}
                aria-label="Back to account"
                className="text-foreground-neutral hover:text-accent-base flex cursor-pointer items-center gap-1 border-none bg-transparent p-0 transition-colors"
            >
                <ArrowShortLeftIcon className="size-3.5 md:size-4" />
                <Text variant="small">Account</Text>
            </button>
            <button
                type="button"
                onClick={(): void => leave("close_button")}
                aria-label="Close migration"
                className="text-foreground-neutral hover:text-accent-base flex cursor-pointer border-none bg-transparent p-0 transition-colors"
            >
                <CrossIcon className="size-5" />
            </button>
        </div>
    );

    if (complete) {
        return (
            <div className="flex h-full flex-col gap-3">
                {header}
                <Heading title="Migration Complete" titleVariant="h1" />
                <div className="border-line-base bg-surface-ground mb-1 flex min-h-0 flex-1 flex-col border p-3 md:p-6">
                    <div className="mb-4 md:mb-5">
                        <AmountInput
                            label={null}
                            mode="display"
                            value={
                                usdvBalance
                                    ? formatUsdvMigrationAmount(usdvBalance)
                                    : ""
                            }
                            loading={!usdvBalance}
                            valueVariant="number-lg"
                            fit
                            tokenChip={
                                <TokenChip
                                    icon={USDvIcon}
                                    symbol="USDv"
                                    textVariant="small"
                                    rounded
                                />
                            }
                        />
                    </div>
                    <Text
                        variant="small"
                        render={<p role="status" />}
                        className="text-foreground-neutral"
                    >
                        You&apos;ve successfully finished migrating to the new
                        USDv. You will be taken back to your account screen
                        shortly.
                    </Text>
                </div>
            </div>
        );
    }

    if (!step) return null;

    const amount = formatUsdvMigrationAmount(step.amount);
    const inFlight = isInFlight(phase);
    // Mirrors the forms' derivation (see UnstakeForm's buttonStatus), with the
    // settled state added: the CTA is where this flow reports, the same way it
    // reports pending and error.
    const buttonStatus =
        phase === "error"
            ? ("error" as const)
            : inFlight
              ? ("pending" as const)
              : succeeded
                ? ("success" as const)
                : undefined;
    const ctaVerb = succeeded
        ? (STEP_ACTION_DONE[step.action] ?? step.action)
        : inFlight
          ? (STEP_ACTION_PENDING[step.action] ?? step.action)
          : step.action;
    const cooldownLeft =
        step.waiting && claimEndsAt === undefined
            ? "—"
            : formatCooldownLeft((claimEndsAt ?? now) - now);
    const receipt = getStepReceipt(step, displayPlan, amount, cooldownLeft);
    if (step.index === 3 || step.index === 4) {
        const outputSymbol =
            step.index === 3 && !displayPlan.directSwap ? "USDC" : "USDv";
        receipt.summaryLabel = "You Receive";
        receipt.summaryValue = receiveAmount
            ? `${receiveAmount} ${outputSymbol}`
            : "Fetching quote";
        if (minimumReceiveAmount)
            receipt.rows.push({
                label: "Minimum receive",
                value: `${minimumReceiveAmount} ${outputSymbol}`
            });
    }
    const ctaLabel = `${ctaVerb} ${receipt.rows[0]?.value ?? amount}`;

    return (
        // Three bands: heading, contents, CTA. The contents take the slack so
        // the action sits on the floor of the popup, whose height is locked
        // when migration starts.
        <div className="flex h-full flex-col gap-3">
            {header}

            <Heading
                title={step.title}
                titleVariant="h1"
                actions={
                    <Text variant="small" className="text-foreground-neutral">
                        Step {stepPosition}/{stepTotal}
                    </Text>
                }
            />

            {/* The framed field + receipt card, matching the swap widget's
                own container (SwapContent.tsx:566). */}
            <div className="border-line-base bg-surface-ground flex min-h-0 flex-1 flex-col border p-3 md:p-6">
                <div className="mb-4 md:mb-5">
                    <AmountInput
                        label={null}
                        value={amount}
                        valueVariant="number-lg"
                        fit
                        tokenChip={
                            <TokenChip
                                icon={STEP_ICON[step.index] ?? USDvIcon}
                                symbol={STEP_SYMBOL[step.index] ?? "USDv"}
                                textVariant="small"
                                rounded
                            />
                        }
                    />
                </div>

                <ReceiptBlock
                    rows={receipt.rows}
                    summaryLabel={receipt.summaryLabel}
                    summaryValue={receipt.summaryValue}
                    textVariant="small"
                />

                {receipt.footnote && (
                    <Text
                        variant="fine-print"
                        render={<p />}
                        className="text-foreground-neutral mt-auto pt-3"
                    >
                        {receipt.footnote}
                    </Text>
                )}
            </div>

            {error && (
                <Text variant="small" render={<p role="alert" />}>
                    {error}
                </Text>
            )}
            {step.waiting && coolingEndsAt ? (
                <TooltipProvider>
                    <Tooltip>
                        {/* status="pending" disables the button, and the base
                            class carries disabled:pointer-events-none, so the
                            hover has to land on this wrapper. */}
                        <TooltipTrigger
                            render={
                                <span className="block w-full cursor-help pb-1" />
                            }
                        >
                            <Button
                                variant="raised"
                                size="lg"
                                interaction="light-bar"
                                status="pending"
                                fullWidth
                                trailing={<TooltipIcon className="size-5" />}
                            >
                                Pending Cooldown
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                            Claim on{" "}
                            {formatUsdvMigrationDateTime(coolingEndsAt)}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            ) : (
                <Button
                    variant="raised"
                    size="lg"
                    interaction="light-bar"
                    fullWidth
                    className="mb-1"
                    status={buttonStatus}
                    disabled={disabled || inFlight || succeeded}
                    onClick={succeeded ? undefined : onAction}
                >
                    {ctaLabel}
                </Button>
            )}
        </div>
    );
}
